import { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { motion } from "framer-motion";

const API_URL = "https://backend.aktcorp.com/pdfs/add";

// Predefined category options
const categories = [
    "STEEL STRUCTURE",
    "SEALENTS",
    "AIRCRAFT MAINTENANCE SEALANT (RTV)",
    "ADHESIVES",
    "ROOF COATINGS",
    "AKT AUTOMOTIVE",
    "TIRE-REINFORCEMENT",
    "AKT TOOLS"
];


const AddPdf = ({ onClose }) => {
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("");
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState({ title: false, category: false, file: false });
    const [showPage, setShowPage] = useState(false);

    // 🔐 Example: Fetch JWT token from localStorage (or context or wherever you store it)
    const jwtToken = localStorage.getItem("authToken"); // ⚠️ Adjust key as per your app

    // Reference to the file input field
    const fileInputRef = useRef(null);

    useEffect(() => {
        setTimeout(() => {
            setShowPage(true);
        }, 2000); // ✅ Show animation for 2 seconds before displaying the form
    }, []);

    useEffect(() => {
        if (Object.values(errors).some((err) => err)) {
            const timeout = setTimeout(() => {
                setErrors({ title: false, category: false, file: false });
            }, 3000);
            return () => clearTimeout(timeout);
        }
    }, [errors]);

    useEffect(() => {
        if (success) {
            const timeout = setTimeout(() => {
                setSuccess(false);
            }, 4000);
            return () => clearTimeout(timeout);
        }
    }, [success]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setErrors((prev) => ({ ...prev, file: false }));
        }
    };

const handleSubmit = async () => {
    const newErrors = { title: !title, category: !category, file: !file };
    if (newErrors.title || newErrors.category || newErrors.file) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = async () => {
        const fileContent = reader.result.split(",")[1];

        await axios.post(
          API_URL,
          {
            title,
            category,
            fileContent,
          },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
              // "x-api-key": API_KEY, // ✅ Uncomment if required
            },
          }
        );
        console.log(fileContent); // Add this before axios.post

        setTimeout(() => {
          setLoading(false);
          setSuccess(true);
          setTitle("");
          setCategory("");
          setFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }, 2000);
      };
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.data?.message || error.message || "Upload failed", { duration: 3000 });
    }
  };
    if (!showPage) {
        return (
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="w-24 h-24 border-4 border-t-transparent border-blue-600 rounded-full mx-auto mt-32"
            ></motion.div>
        );
    }

    return (
        <div className="p-6 max-w-md mx-auto bg-white rounded-xl shadow-md text-center">
            <motion.h2
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-3xl font-bold text-gray-900 bg-clip-text text-center mb-4 text-transparent bg-gradient-to-r from-blue-500 to-purple-500"
            >
                Add New PDF
            </motion.h2>

            <div className="space-y-4 text-left">
                <label className="text-gray-600 font-medium">PDF Name :</label>
                <input type="text" placeholder="Enter PDF Name" value={title} onChange={(e) => setTitle(e.target.value)}
                    className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none mt-1 ${errors.title ? "border-red-500" : ""}`}
                />
                {errors.title && <p className="text-red-500 text-sm">*Name is required</p>}

                <label className="text-gray-600 font-medium">Select Page :</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                    className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none mt-1 ${errors.category ? "border-red-500" : ""}`}>
                    <option value="">Select a Page</option>
                    {categories.map((cat, index) => <option key={index} value={cat}>{cat}</option>)}
                </select>
                {errors.category && <p className="text-red-500 text-sm">*Page Selection is required</p>}

                <label className="text-gray-600 font-medium">Upload PDF :</label>
                <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileChange}
                    className="w-full p-2 border rounded-lg bg-gray-100 mt-1" />
                {errors.file && <p className="text-red-500 text-sm">*PDF file is required</p>}

                {/* Show animation for 2 seconds after submitting */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                        <p className="text-lg font-medium text-blue-500">Uploading...</p>
                    </div>
                ) : (
                    <button onClick={handleSubmit} className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition">
                        Upload
                    </button>
                )}
            </div>

            {/* Success message appears after upload and disappears after 3 seconds */}
            {success && (
                <p className="mt-4 text-green-600 font-medium">PDF uploaded successfully!</p>
            )}
        </div>
    );
};

export default AddPdf;
